// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { StyledBox } from "@pace/XDL/Utilities";
import { mergeWithCss } from "@pace/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { FC, useState } from "react";

export interface ProductDetailsReplacementAnchorStyle {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const productDetailsReplacementAnchorStyles: ProductDetailsReplacementAnchorStyle = {};

interface OwnProps {
    extendedStyles?: ProductDetailsReplacementAnchorStyle;
    reference: React.RefObject<HTMLDivElement>;
}

type Props = OwnProps & HasProductContext;
const ProductDetailsReplacementAnchor: FC<Props> = ({ extendedStyles, productContext, reference }) => {
    const [styles] = useState(() => mergeWithCss(productDetailsReplacementAnchorStyles, extendedStyles));
    if (!productContext?.product?.detail?.replacementProductId) {
        return null;
    }

    const goToReplacementElement = () => {
        window.scrollTo({
            top: reference?.current ? reference?.current?.offsetTop - 150 : 0,
            behavior: "smooth",
        });
    };

    return (
        <StyledBox {...styles.wrapper}>
            <Typography {...styles.title}>{translate("Replacement Item")}</Typography>
            <Typography {...styles.description}>{siteMessage("Custom_Alternative_Item")}</Typography>
            <Link {...styles.link} onClick={goToReplacementElement} data-test-selector="replacement-anchor">
                {translate("View Replacement Item")}
            </Link>
        </StyledBox>
    );
};

export default withProductContext(ProductDetailsReplacementAnchor);
